import { ConnectedWallet } from "@goblinhunt/cosmes/wallet";
import { toBase64 } from "@cosmjs/encoding";

export type SignMethod =
  | "cosmes:signArbitrary"
  | "cosmes:adr36_amino_msgsigdata";

export type SignedVoucher = {
  address: string;
  message: string;
  method: SignMethod;
  pubkey:string
  pubkeyBase64: string;        // base64 (33/65 bytes)
  signatureBase64: string;     // base64 (DER atau compact)
  sigFormat: "DER" | "compact";
  signBytesBase64?: string;    // kalau wallet kasih signDocBytes
  digestHex?: string;          // kalau wallet cuma balikin hash (hex) seperti "data"
  walletLabel?: string;
};

const enc = new TextEncoder();

function guessSigFormat(sigRaw: Uint8Array): "DER" | "compact" {
  return sigRaw.length > 0 && sigRaw[0] === 0x30 ? "DER" : (sigRaw.length === 64 ? "compact" : "DER");
}

function b64ToBytes(b64: string): Uint8Array {
  if (typeof atob === "function") {
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return arr;
  }
  // Node fallback (optional, kalau dipakai di SSR)
  return Uint8Array.from(Buffer.from(b64, "base64"));
}

function buildAdr36AminoSignBytes(address: string, messageUtf8: string): Uint8Array {
  const dataB64 = toBase64(enc.encode(messageUtf8));
  const signDoc = {
    account_number: "0",
    chain_id: "",
    fee: { amount: [], gas: "0" },
    memo: "",
    msgs: [{ type: "sign/MsgSignData", value: { signer: address, data: dataB64 } }],
    sequence: "0",
  };
  return new TextEncoder().encode(JSON.stringify(signDoc));
}

export async function signWithAnyWallet(
  wallet: ConnectedWallet,
  opts: {address: string; message: string }
): Promise<SignedVoucher> {
  const { address, message } = opts;
    console.log(typeof (wallet as any).signAmino === "function")
  try {
    const r: any = await wallet.signArbitrary(message);
    console.log(r)
    const signatureBase64: string =
      typeof r.signature === "string" ? r.signature : toBase64(r.signature);
    const pubkeyBase64: string =
      r.pubKey ?? (r.pubKey ? toBase64(r.pubKey) : "");

    if (!signatureBase64 || !pubkeyBase64) {
      throw new Error("signArbitrary: pubkey/signature kosong.");
    }

    // Deteksi format signature
    const sigRaw = typeof r.signature === "string" ? b64ToBytes(r.signature) : r.signature;
    const sigFormat = guessSigFormat(sigRaw);

    // Kalau wallet ngasih signDocBytes, simpan juga
    const signBytesBase64: string | undefined =
      r.signDocBytes ? toBase64(r.signDocBytes) : undefined;

    return {
      address,
      message,
      method: "cosmes:signArbitrary",
      pubkey:r.pubkey,
      pubkeyBase64,
      signatureBase64,
      sigFormat,
      signBytesBase64,
      // Wallet kamu mengirim "data" (64 hex) → kemungkinan sha256(sign_bytes)
      digestHex: typeof r.data === "string" ? r.data : undefined,
      walletLabel: (r as any)?.wallet ?? undefined,
    };
  } catch {
    // lanjut ke fallback
  }

  // 2) Fallback: ADR-36 via MsgSignData (amino)
  const signBytes = buildAdr36AminoSignBytes(address, message);

  if (typeof (wallet as any).signAmino === "function") {
    const aminoDoc = {
      chain_id: "",
      account_number: "0",
      sequence: "0",
      fee: { amount: [], gas: "0" },
      msgs: [{ type: "sign/MsgSignData", value: { signer: address, data: toBase64(enc.encode(message)) } }],
      memo: "",
    };
    const r: any = await (wallet as any).signAmino(address, aminoDoc);

    const sigB64: string = r?.signature ?? r?.sig ?? "";
    const pubB64: string = r?.pub_key?.value ?? r?.pubkey ?? "";

    if (!sigB64 || !pubB64) throw new Error("ADR-36 amino: pubkey/signature kosong.");

    const sigRaw = b64ToBytes(sigB64);

    return {
      address,
      message,
      method: "cosmes:adr36_amino_msgsigdata",
      pubkey: r.pubkey,
      pubkeyBase64: pubB64,
      signatureBase64: sigB64,
      sigFormat: guessSigFormat(sigRaw),
      signBytesBase64: toBase64(signBytes),
      walletLabel: (r as any)?.wallet ?? undefined,
    };
  }

  throw new Error("Tidak ada metode signing yang tersedia dari wallet terhubung.");
}
